import React, { useMemo } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';

import { useAuthenticatedState } from 'src/authenticated-state/context';

import styles from 'src/components/input/select-ingestion-source/SelectIngestionSource.module.css';

type SourceOption = {
    /** Present only on the synthetic "create new source" entry. */
    inputValue?: string,
    label: string,
};

const filter = createFilterOptions<SourceOption>();

type Props = {
    value: string,
    onChange: (value: string) => void,
    label?: string,
    placeholder?: string,
    error?: boolean,
    helperText?: React.ReactNode,
    required?: boolean,
    'data-testid'?: string,
};

export default function SelectIngestionSource({
    value,
    onChange,
    label = 'Ingestion Source',
    placeholder = 'Apartments.com',
    error,
    helperText,
    required,
    'data-testid': dataTestId,
}: Props) {
    const { leadIngestionSources } = useAuthenticatedState();

    const options = useMemo<SourceOption[]>(() => {
        return leadIngestionSources.map((source) => ({ label: source }));
    }, [leadIngestionSources]);

    return (
        <Autocomplete<SourceOption, false, false, true>
            freeSolo
            selectOnFocus
            handleHomeEndKeys
            options={options}
            value={value}
            // Drive the form value off the input text so a typed-but-not-selected
            // source is always committed (selecting an option also updates the input).
            onInputChange={(_, newInputValue) => onChange(newInputValue)}
            filterOptions={(opts, params) => {
                const filtered = filter(opts, params);
                const { inputValue } = params;
                const isExisting = opts.some((option) => inputValue === option.label);
                if (inputValue !== '' && !isExisting) {
                    // New source: shown as-is with a muted "adds new source" tag.
                    filtered.push({
                        inputValue,
                        label: inputValue,
                    });
                }
                return filtered;
            }}
            getOptionLabel={(option) => {
                if (typeof option === 'string') {
                    return option;
                }
                return option.label;
            }}
            renderOption={({ key, ...optionProps }, option) => (
                <li key={key} {...optionProps}>
                    <span className={styles.optionLabel}>{option.label}</span>
                    {option.inputValue && (
                        <span className={styles.newSourceTag}>adds new source</span>
                    )}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="standard"
                    label={label}
                    placeholder={placeholder}
                    error={error}
                    helperText={helperText}
                    required={required}
                    data-testid={dataTestId}
                    InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                />
            )}
        />
    );
}
